import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@/lib/api'
import { Settings } from 'lucide-react'

export default function PlatformSettingsPage() {
  const qc = useQueryClient()
  const [paymentInfo, setPaymentInfo] = useState('')
  const [qrisImageUrl, setQrisImageUrl] = useState('')
  const [saved, setSaved] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'platform-settings'],
    queryFn: adminApi.getPlatformSettings,
  })

  useEffect(() => {
    if (data) {
      setPaymentInfo(data.payment_info ?? '')
      setQrisImageUrl(data.qris_image_url ?? '')
    }
  }, [data])

  const saveMutation = useMutation({
    mutationFn: () =>
      adminApi.updatePlatformSettings({
        payment_info: paymentInfo.trim() || null,
        qris_image_url: qrisImageUrl.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'platform-settings'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-2xl">
        <h1 className="text-2xl font-bold">Pengaturan Platform</h1>
        <div className="h-48 animate-pulse rounded-lg border bg-muted" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Pengaturan Platform</h1>
        <p className="text-sm text-muted-foreground">
          Konfigurasi info pembayaran platform yang ditampilkan ke tenant saat upgrade plan.
        </p>
      </div>

      {/* Payment Instructions */}
      <div className="rounded-xl border bg-card p-6 space-y-4">
        <div>
          <h2 className="font-semibold">Instruksi Pembayaran</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Teks ini tampil di popup upgrade plan milik tenant — berisi nomor rekening / info
            transfer untuk membayar langganan ke platform.
          </p>
        </div>
        <textarea
          rows={7}
          value={paymentInfo}
          onChange={(e) => setPaymentInfo(e.target.value)}
          placeholder={`Contoh:\nBank BCA\nNo. Rekening: 1234567890\nA.n. OneIdol Platform\n\nSetelah transfer, upload screenshot bukti pembayaran.`}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none resize-none"
        />
      </div>

      {/* QRIS Image URL */}
      <div className="rounded-xl border bg-card p-6 space-y-4">
        <div>
          <h2 className="font-semibold">URL Gambar QRIS</h2>
          <p className="text-sm text-muted-foreground mt-1">
            URL gambar QRIS platform. Upload gambar ke storage terlebih dahulu lalu paste URL-nya di sini.
            Gambar ini ditampilkan di popup upgrade plan sebagai panduan pembayaran QRIS.
          </p>
        </div>
        <input
          type="url"
          value={qrisImageUrl}
          onChange={(e) => setQrisImageUrl(e.target.value)}
          placeholder="https://..."
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />
        {qrisImageUrl && (
          <div className="flex items-start gap-4">
            <div className="h-40 w-40 shrink-0 rounded-lg border border-border bg-white overflow-hidden flex items-center justify-center">
              <img
                src={qrisImageUrl}
                alt="QRIS Preview"
                className="max-h-full max-w-full object-contain p-2"
                onError={(e) => {
                  ;(e.target as HTMLImageElement).style.display = 'none'
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground pt-1">Preview QRIS</p>
          </div>
        )}
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {saveMutation.isPending ? 'Menyimpan...' : 'Simpan Perubahan'}
        </button>
        {saved && <p className="text-sm text-green-600">Tersimpan!</p>}
        {saveMutation.error && (
          <p className="text-sm text-destructive">
            {(saveMutation.error as Error).message ?? 'Gagal menyimpan.'}
          </p>
        )}
      </div>

      {/* Info */}
      <div className="flex items-start gap-3 rounded-xl border border-dashed border-muted-foreground/30 bg-muted/20 px-4 py-3">
        <Settings className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">
          Perubahan langsung berlaku di popup upgrade plan setiap tenant. Pastikan info rekening
          sudah benar sebelum menyimpan.
        </p>
      </div>
    </div>
  )
}
